import {
  addComment,
  ClauseComment,
  ReactionSummary,
  removeCommentFromTree,
  replaceComment,
  updateCommentReactions,
} from "@asksyntheia/comments";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery, QueryArg } from "../axiosConfig";
import { RequestMethod } from "../enum";
import {
  CreateCommentArgs,
  CreateCommentResponse,
  DeleteCommentArgs,
  GetCommentsArgs,
  GetCommentsResponse,
  ToggleReactionArgs,
  ToggleReactionResponse,
  UpdateCommentArgs,
  UpdateCommentResponse,
} from "./types";

const getCommentsQuery = ({ workspaceId, filter }: GetCommentsArgs): QueryArg => ({
  url: "/search",
  method: RequestMethod.POST,
  data: { filter },
  workspaceId,
});

const createCommentQuery = ({ workspaceId, comment }: CreateCommentArgs): QueryArg => ({
  url: "",
  method: RequestMethod.POST,
  data: { ...comment },
  workspaceId,
});

const updateCommentQuery = ({ workspaceId, comment: { commentId, ...data } }: UpdateCommentArgs): QueryArg => ({
  url: `/${commentId}`,
  method: RequestMethod.PUT,
  data,
  workspaceId,
});

const deleteCommentQuery = ({ workspaceId, commentId }: DeleteCommentArgs): QueryArg => ({
  url: `/${commentId}`,
  method: RequestMethod.DELETE,
  workspaceId,
});

const toggleReactionQuery = ({ workspaceId, commentId, emoji }: ToggleReactionArgs): QueryArg => ({
  url: `/${commentId}/reactions`,
  method: RequestMethod.POST,
  data: { emoji },
  workspaceId,
});

export const commentApi = createApi({
  reducerPath: "commentApi",
  baseQuery: axiosBaseQuery({ baseUrl: "/comments" }),
  tagTypes: ["Comments"],
  endpoints: (build) => ({
    getComments: build.query<ClauseComment[], GetCommentsArgs>({
      query: getCommentsQuery,
      transformResponse: (response: GetCommentsResponse) => response.comments,
      providesTags: ["Comments"],
    }),
    createComment: build.mutation<ClauseComment, CreateCommentArgs>({
      query: createCommentQuery,
      transformResponse: (response: CreateCommentResponse) => response.comment,
      onQueryStarted: async (args, { dispatch, queryFulfilled, getState }) => {
        const result = await queryFulfilled.catch(() => null);
        if (!result) return;
        const newComment = result.data;
        const searches = commentApi.util.selectCachedArgsForQuery(getState(), "getComments");

        searches
          .filter(
            (search) =>
              search.workspaceId === args.workspaceId &&
              search.filter.relatedId === newComment.relatedId &&
              (!search.filter.clauseId || search.filter.clauseId === newComment.clauseId)
          )
          .forEach((search) => {
            dispatch(commentApi.util.updateQueryData("getComments", search, addComment(newComment)));
          });
      },
    }),
    updateComment: build.mutation<ClauseComment, UpdateCommentArgs>({
      query: updateCommentQuery,
      transformResponse: (response: UpdateCommentResponse) => response.comment,
      onQueryStarted: async (args, { dispatch, queryFulfilled, getState }) => {
        const result = await queryFulfilled.catch(() => null);
        if (!result) return;
        const updatedComment = result.data;
        const searches = commentApi.util.selectCachedArgsForQuery(getState(), "getComments");

        searches
          .filter((search) => search.workspaceId === args.workspaceId)
          .forEach((search) => {
            dispatch(commentApi.util.updateQueryData("getComments", search, replaceComment(updatedComment)));
          });
      },
    }),
    deleteComment: build.mutation<void, DeleteCommentArgs>({
      query: deleteCommentQuery,
      onQueryStarted: async (args, { dispatch, queryFulfilled, getState }) => {
        const result = await queryFulfilled.catch(() => null);
        if (!result) return;
        const searches = commentApi.util.selectCachedArgsForQuery(getState(), "getComments");

        searches
          .filter((search) => search.workspaceId === args.workspaceId)
          .forEach((search) => {
            dispatch(commentApi.util.updateQueryData("getComments", search, removeCommentFromTree(args.commentId)));
          });
      },
    }),
    toggleReaction: build.mutation<ReactionSummary[], ToggleReactionArgs>({
      query: toggleReactionQuery,
      transformResponse: (response: ToggleReactionResponse) => response.reactions,
      onQueryStarted: async (args, { dispatch, queryFulfilled, getState }) => {
        const result = await queryFulfilled.catch(() => null);
        if (!result) return;
        const reactions = result.data;
        const searches = commentApi.util.selectCachedArgsForQuery(getState(), "getComments");

        searches
          .filter((search) => search.workspaceId === args.workspaceId)
          .forEach((search) => {
            dispatch(
              commentApi.util.updateQueryData("getComments", search, updateCommentReactions(args.commentId, reactions))
            );
          });
      },
    }),
  }),
});
